import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {Observable, shareReplay} from "rxjs";
import {Product} from "../../models/product";
import {ShopService} from "../../modules/shop/service/shop.service";
import {CartService} from "../../modules/cart/service/cart.service";
import {AnalyticsService} from "../../services/util/analytics.service";
import {FREE_DELIVERY_THRESHOLD} from "../../services/util/constants";

/** Best sellers, fetched once per visit and shared by every cart view. */
let candidates$: Observable<Product[]> | null = null;

/**
 * Under "Уште X ден. до бесплатна достава": a few products that close the gap, each added
 * with one tap. The cheapest best seller that reaches the threshold comes first.
 */
@Component({
    selector: 'app-cart-addons',
    standalone: true,
    template: `
        @if (suggestions.length > 0) {
            <div class="mt-3 text-left">
                <p class="text-sm font-medium text-gray-800">Додадете и добивате бесплатна достава:</p>
                <ul class="mt-2 space-y-2 list-none p-0">
                    @for (product of suggestions; track product.id) {
                        <li class="flex items-center gap-3 rounded border border-gray-200 bg-white p-2">
                            <img class="h-12 w-12 shrink-0 object-contain" [src]="product.images[0]" [alt]="product.name"
                                 width="48" height="48" loading="lazy">
                            <div class="min-w-0 flex-1">
                                <p class="text-xs sm:text-sm text-gray-900 line-clamp-2">{{ product.name }}</p>
                                <p class="text-sm font-semibold text-gray-900">{{ product.price }} ден.</p>
                            </div>
                            <button type="button"
                                    class="shrink-0 rounded bg-primaryDark px-3 py-1.5 text-sm font-medium text-white hover:bg-primaryDarker"
                                    [attr.aria-label]="'Додади ' + product.name"
                                    (click)="add(product)">+ Додај</button>
                        </li>
                    }
                </ul>
            </div>
        }
    `
})
export class CartAddonsComponent implements OnInit {

    /** Products total, without delivery. */
    @Input()
    orderPrice: number = 0;

    @Input()
    cartProductIds: (number | undefined)[] = [];

    @Input()
    limit: number = 3;

    /** After a product went into the cart - pages that do not listen to the cart refresh with it. */
    @Output()
    added = new EventEmitter<Product>();

    private products: Product[] = [];

    constructor(private readonly shopService: ShopService,
                private readonly cartService: CartService,
                private readonly analytics: AnalyticsService) {
    }

    ngOnInit(): void {
        candidates$ ??= this.shopService.getTopSellingProducts(30).pipe(shareReplay(1));
        candidates$.subscribe({
            next: products => this.products = products,
            error: () => candidates$ = null
        });
    }

    get suggestions(): Product[] {
        const remaining = FREE_DELIVERY_THRESHOLD - (this.orderPrice || 0);
        if (!this.orderPrice || remaining <= 0) {
            return [];
        }
        const inCart = new Set(this.cartProductIds);
        const available = this.products.filter(product => product.price > 0 && !inCart.has(product.id) && product.images?.length);
        const closing = available.filter(product => product.price >= remaining).sort((a, b) => a.price - b.price);
        const others = available.filter(product => product.price < remaining).sort((a, b) => b.price - a.price);
        return [...closing, ...others].slice(0, this.limit);
    }

    add(product: Product): void {
        this.cartService.add({product, quantity: 1});
        this.analytics.addToCart(product, 1);
        this.added.emit(product);
    }
}
